import React, { useState, useEffect } from 'react';
import { X, Loader2, Receipt, Plus, Trash2 } from 'lucide-react';
import { RichTextEditor } from '../RichTextEditor';

export function InvoicePreviewModal({ isOpen, onClose, job, onSubmit, loading }) {
  const [previewData, setPreviewData] = React.useState(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [description, setDescription] = React.useState('');

  React.useEffect(() => {
    if (isOpen && job?.id) {
      setPreviewLoading(true);
      import('../../services/api').then(({ getJobInvoicePreview }) => {
        getJobInvoicePreview(job.id).then(res => {
          setPreviewData(res.data);
          // Auto-populate description from job titles and quote number
          const titles = res.data.descriptions?.map(d => d.job_title).filter(Boolean).join(', ');
          const quoteNum = res.data.quote_number || '';
          let autoDesc = '';
          if (titles && quoteNum) {
            autoDesc = `Works to ${titles} completed as per Quote ${quoteNum}`;
          } else if (titles) {
            autoDesc = `Works to ${titles} completed`;
          } else if (quoteNum) {
            autoDesc = `Works completed as per Quote ${quoteNum}`;
          }
          setDescription(autoDesc);
          setPreviewLoading(false);
        }).catch(() => setPreviewLoading(false));
      });
    }
  }, [isOpen, job?.id]);

  if (!isOpen || !job) return null;

  const depositInfo = previewData?.deposit_info;
  const worksSubtotal = previewData?.works_subtotal || previewData?.subtotal || 0;
  const subtotal = previewData?.subtotal || 0;
  const gst = previewData?.gst || 0;
  const amountDue = previewData?.total || 0;

  const handleCreate = () => {
    const lines = [{ description, amount: worksSubtotal }];
    if (depositInfo) {
      lines.push({
        description: depositInfo.deposit_description,
        amount: -depositInfo.deposit_amount
      });
    }
    onSubmit(lines);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="invoice-preview-modal">
      <div className="bg-white rounded-[10px] w-full max-w-lg max-h-[80vh] overflow-y-auto">
        <div className="p-4 border-b"><h2 className="font-semibold">Create Invoice</h2></div>
        {previewLoading ? (
          <div className="p-12 text-center"><Loader2 size={24} className="animate-spin mx-auto text-primary" /></div>
        ) : (
          <div className="p-6">
            <table className="w-full text-sm mb-4">
              <thead><tr className="border-b-2"><th className="text-left py-1 font-bold">Description</th><th className="text-right py-1 font-bold w-28">Amount</th></tr></thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-2 pr-4">
                    <div className="text-sm">{description}</div>
                  </td>
                  <td className="text-right align-top">{worksSubtotal.toFixed(2)}</td>
                </tr>
                {depositInfo && (
                  <tr className="border-b">
                    <td className="py-2 pr-4">
                      <div className="text-sm text-red-600">{depositInfo.deposit_description}</div>
                    </td>
                    <td className="text-right align-top text-red-600">-{depositInfo.deposit_amount.toFixed(2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="text-right text-sm space-y-1 border-t pt-3">
              <div>Sub Total <span className="ml-8 font-semibold">{subtotal.toFixed(2)}</span></div>
              <div>Total GST 10% <span className="ml-8 font-semibold">{gst.toFixed(2)}</span></div>
              <div className="border-t pt-1 mt-1 font-bold">Amount Due AUD <span className="ml-8">{amountDue.toFixed(2)}</span></div>
            </div>
          </div>
        )}
        <div className="p-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm">Cancel</button>
          <button
            onClick={handleCreate}
            disabled={loading || previewLoading}
            className="px-4 py-2 text-sm bg-primary text-white rounded-sm flex items-center gap-2"
            data-testid="invoice-create-submit"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}Create Invoice
          </button>
        </div>
      </div>
    </div>
  );
}

// Client-side image resize to reduce file size before upload
